import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import Leave from './Leave'
import Work from './Work'
import AS from './AS'
import Members from './Members'
import DashboardHome from './DashboardHome'
import Foreign from './Foreign'
import Delivery from './Delivery'
import Invoice from './Invoice'
import Order from './Order'
import Quote from './Quote'
import Clients from './Clients'
import Drawings from './Drawings'
import Inventory from './Inventory'

const DESKTOP_MIN = 768

const PAGE_IDS = ['dashboard','leave','work','drawings','inventory','as','delivery','invoice','order','quote','clients','foreign','members']

/** URL 해시(#leave 등)에서 현재 페이지 읽기 — 없거나 잘못되면 대시보드 */
function readPageFromHash() {
  const h = (window.location.hash || '').replace(/^#\/?/, '')
  return PAGE_IDS.includes(h) ? h : 'dashboard'
}

export default function Dashboard({ profile }) {
  // 페이지를 URL 해시와 동기화 — 새로고침 시 현재 화면 유지, 뒤로가기 시 이전 화면으로
  const [page, setPageState] = useState(readPageFromHash)
  const setPage = (p) => {
    if (readPageFromHash() === p) { setPageState(p); return }
    window.location.hash = p // history에 기록 → 뒤로가기로 복귀 가능
  }
  useEffect(() => {
    const onHash = () => setPageState(readPageFromHash())
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= DESKTOP_MIN : false
  )
  const [pendingCount, setPendingCount] = useState(0)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const isAdmin = profile.role === 'admin'

  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${DESKTOP_MIN}px)`)
    const apply = () => setIsDesktop(mq.matches)
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [])

  // 관리자: 연차 승인 대기 건수 (페이지 이동 시 + 1분마다 갱신)
  useEffect(() => {
    if (!isAdmin) return
    let active = true
    async function fetchPending() {
      const { count } = await supabase
        .from('leave_requests')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'pending')
      if (active) setPendingCount(count || 0)
    }
    fetchPending()
    const timer = setInterval(fetchPending, 60000)
    return () => { active = false; clearInterval(timer) }
  }, [isAdmin, page])

  async function handleLogout() {
    await supabase.auth.signOut()
  }

  /* 비밀번호 변경 */
  const [pwOpen, setPwOpen] = useState(false)
  const [pwForm, setPwForm] = useState({ a:'', b:'' })
  const [pwLoading, setPwLoading] = useState(false)

  async function changePassword() {
    if (pwForm.a.length < 6) { alert('비밀번호는 6자 이상이어야 합니다'); return }
    if (pwForm.a !== pwForm.b) { alert('비밀번호가 서로 일치하지 않습니다'); return }
    setPwLoading(true)
    const { error } = await supabase.auth.updateUser({ password: pwForm.a })
    setPwLoading(false)
    if (error) { alert('변경 실패: ' + error.message); return }
    setPwOpen(false)
    setPwForm({ a:'', b:'' })
    alert('비밀번호가 변경되었습니다. 다음 로그인부터 새 비밀번호를 사용하세요.')
  }

  const pwModal = pwOpen && (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={e => e.target === e.currentTarget && setPwOpen(false)}>
      <div style={{ background:'#fff', borderRadius:14, width:360, maxWidth:'95vw', boxShadow:'0 20px 60px rgba(0,0,0,0.15)', color:'#1a1916' }}>
        <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #e5e3de', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ fontSize:15, fontWeight:700 }}>🔑 비밀번호 변경</span>
          <button onClick={() => setPwOpen(false)} style={{ border:'none', background:'#f9f8f6', borderRadius:6, width:28, height:28, cursor:'pointer', fontSize:16 }}>✕</button>
        </div>
        <div style={{ padding:'20px 24px' }}>
          <div style={{ marginBottom:14 }}>
            <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>새 비밀번호</label>
            <input type="password" value={pwForm.a} onChange={e => setPwForm({...pwForm, a:e.target.value})}
              placeholder="6자 이상"
              style={{ width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', boxSizing:'border-box' }} />
          </div>
          <div>
            <label style={{ display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>새 비밀번호 확인</label>
            <input type="password" value={pwForm.b} onChange={e => setPwForm({...pwForm, b:e.target.value})}
              placeholder="한 번 더 입력"
              style={{ width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', boxSizing:'border-box' }} />
          </div>
        </div>
        <div style={{ padding:'16px 24px', borderTop:'1px solid #e5e3de', display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button onClick={() => setPwOpen(false)}
            style={{ padding:'8px 16px', border:'1px solid #e5e3de', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:13 }}>취소</button>
          <button onClick={changePassword} disabled={pwLoading}
            style={{ padding:'8px 16px', border:'none', borderRadius:8, background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>
            {pwLoading ? '변경 중...' : '변경하기'}
          </button>
        </div>
      </div>
    </div>
  )

  /* 메뉴 그룹 — 데스크톱 사이드바 · 모바일 드로어 공용 */
  const navGroups = [
    { label: null, items: [
      { id: 'dashboard', icon: '🏠', label: '대시보드' },
    ]},
    { label: '현장 업무', items: [
      { id: 'work',     icon: '🚛', label: '제작 이력' },
      { id: 'drawings', icon: '📐', label: '양산도면' },
      { id: 'inventory',icon: '🪨', label: '자재 재고' },
      { id: 'as',       icon: '🔧', label: 'AS 관리' },
      { id: 'delivery', icon: '🚚', label: '출고 관리' },
    ]},
    { label: '문서 작성', items: [
      { id: 'invoice', icon: '🧾', label: '거래명세표' },
      { id: 'order',   icon: '📋', label: '발주서' },
      { id: 'quote',   icon: '📄', label: '견적서' },
      { id: 'clients', icon: '🏢', label: '거래처' },
    ]},
    { label: '인사 관리', items: [
      { id: 'leave',   icon: '📅', label: '연차 관리', badge: isAdmin ? pendingCount : 0 },
      { id: 'foreign', icon: '👷', label: '외국인 관리' },
      ...(isAdmin ? [{ id: 'members', icon: '👥', label: '직원 관리' }] : []),
    ]},
  ]

  const pageTitle = {
    dashboard:'대시보드', leave:'연차 관리', work:'특장차 제작 이력', drawings:'양산도면', inventory:'원자재 재고', as:'AS 관리',
    delivery:'출고 관리', invoice:'거래명세표', order:'발주서', quote:'견적서',
    clients:'거래처 관리', foreign:'외국인 관리', members:'직원 관리',
  }

  const badgeStyle = {
    background:'#b83030', color:'#fff', borderRadius:10, fontSize:10, fontWeight:700,
    minWidth:16, height:16, padding:'0 4px', display:'inline-flex',
    alignItems:'center', justifyContent:'center', lineHeight:1,
  }

  function renderPage() {
    switch (page) {
      case 'leave':    return <Leave profile={profile} />
      case 'work':     return <Work profile={profile} />
      case 'drawings': return <Drawings profile={profile} />
      case 'inventory': return <Inventory profile={profile} />
      case 'as':       return <AS profile={profile} />
      case 'delivery': return <Delivery profile={profile} />
      case 'invoice':  return <Invoice profile={profile} />
      case 'order':    return <Order profile={profile} />
      case 'quote':    return <Quote profile={profile} />
      case 'clients':  return <Clients />
      case 'foreign':  return <Foreign profile={profile} />
      case 'members':  return <Members profile={profile} />
      default:         return <DashboardHome profile={profile} setPage={setPage} />
    }
  }

  /* 그룹 메뉴 목록 (사이드바·드로어 공용) */
  function renderNav(onSelect) {
    return navGroups.map((group, gi) => (
      <div key={gi} style={{ marginBottom: 4 }}>
        {group.label && (
          <div style={{
            padding:'14px 20px 6px', fontSize:10, fontWeight:700, letterSpacing:'0.08em',
            color:'rgba(255,255,255,0.35)',
          }}>
            {group.label}
          </div>
        )}
        {group.items.map(item => (
          <div
            key={item.id}
            role="button"
            tabIndex={0}
            onClick={() => onSelect(item.id)}
            onKeyDown={e => e.key === 'Enter' && onSelect(item.id)}
            style={{
              display:'flex', alignItems:'center', gap:10,
              padding:'10px 20px', cursor:'pointer', fontSize:13,
              color: page === item.id ? '#fff' : 'rgba(255,255,255,0.6)',
              background: page === item.id ? 'rgba(255,255,255,0.1)' : 'transparent',
              borderLeft: page === item.id ? '3px solid #4a8c60' : '3px solid transparent',
              transition:'all 0.15s',
            }}
          >
            <span style={{ fontSize:16, width:20, textAlign:'center' }}>{item.icon}</span>
            <span style={{ flex:1 }}>{item.label}</span>
            {item.badge > 0 && <span style={badgeStyle}>{item.badge > 99 ? '99+' : item.badge}</span>}
          </div>
        ))}
      </div>
    ))
  }

  /* ——— 데스크톱: 사이드바 + 메인 ——— */
  if (isDesktop) {
    return (
      <div style={{ display:'flex', minHeight:'100vh', fontFamily:'Noto Sans KR, sans-serif' }}>

        <aside style={{
          width:220, background:'#1a1916', color:'#fff', display:'flex', flexDirection:'column',
          position:'fixed', top:0, left:0, bottom:0, zIndex:100,
        }}>
          <div style={{ padding:'24px 20px 20px', borderBottom:'1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ fontSize:11, color:'rgba(255,255,255,0.5)', letterSpacing:'0.05em', marginBottom:4 }}>특장차 관리 시스템</div>
            <div style={{ fontSize:15, fontWeight:700 }}>사내 포털</div>
          </div>

          <nav style={{ padding:'8px 0', flex:1, overflowY:'auto' }}>
            {renderNav(setPage)}
          </nav>

          <div style={{ padding:'16px 20px', borderTop:'1px solid rgba(255,255,255,0.1)', display:'flex', alignItems:'center', gap:10 }}>
            <div style={{
              width:32, height:32, borderRadius:'50%', background:'#4a8c60', display:'flex',
              alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, flexShrink:0,
            }}>
              {profile.name[0]}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:13, fontWeight:500 }}>{profile.name}</div>
              <div style={{ fontSize:11, color:'rgba(255,255,255,0.45)', marginTop:1 }}>
                {profile.role === 'admin' ? '관리자' : profile.dept + ' · 직원'}
              </div>
            </div>
            <span
              onClick={() => setPwOpen(true)}
              style={{ fontSize:12, color:'rgba(255,255,255,0.4)', cursor:'pointer', padding:'4px 4px', borderRadius:4 }}
              title="비밀번호 변경"
            >
              🔑
            </span>
            <span
              onClick={handleLogout}
              style={{ fontSize:11, color:'rgba(255,255,255,0.4)', cursor:'pointer', padding:'4px 6px', borderRadius:4 }}
              title="로그아웃"
            >
              ↩
            </span>
          </div>
        </aside>

        <div style={{ marginLeft:220, flex:1, display:'flex', flexDirection:'column', background:'#f4f3f0', minWidth:0 }}>
          <div style={{
            background:'#fff', borderBottom:'1px solid #e5e3de', padding:'0 28px', height:56,
            display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:50,
          }}>
            <div style={{ fontSize:16, fontWeight:700 }}>{pageTitle[page]}</div>
            <span style={{
              fontSize:11, padding:'3px 10px', borderRadius:20, fontWeight:600,
              background: profile.role === 'admin' ? '#e8f2eb' : '#e8eef8',
              color: profile.role === 'admin' ? '#2d5a3d' : '#1e4a8c',
            }}>
              {profile.role === 'admin' ? '관리자' : '직원'}
            </span>
          </div>

          <div style={{ flex:1, padding:28, overflowY:'auto' }}>
            {renderPage()}
          </div>
        </div>
        {pwModal}
      </div>
    )
  }

  /* ——— 모바일: 탑바(☰ 드로어) ——— */
  return (
    <div style={{ display:'flex', flexDirection:'column', minHeight:'100vh', fontFamily:'Noto Sans KR, sans-serif', background:'#f4f3f0' }}>

      {/* 탑바 */}
      <div style={{
        background:'#1a1916', padding:'0 12px 0 8px', height:52, display:'flex', alignItems:'center',
        justifyContent:'space-between', position:'sticky', top:0, zIndex:50, gap:8,
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:4, minWidth:0 }}>
          {/* 메뉴 버튼 */}
          <button type="button" onClick={() => setDrawerOpen(true)}
            style={{
              border:'none', background:'transparent', color:'#fff', fontSize:20,
              width:40, height:40, cursor:'pointer', position:'relative', flexShrink:0,
              display:'flex', alignItems:'center', justifyContent:'center',
            }}>
            ☰
            {pendingCount > 0 && (
              <span style={{ position:'absolute', top:6, right:5, width:8, height:8, borderRadius:'50%', background:'#b83030' }} />
            )}
          </button>
          <span style={{ fontSize:15, fontWeight:700, color:'#fff', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {pageTitle[page]}
          </span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
          <span style={{
            fontSize:11, padding:'3px 10px', borderRadius:20, fontWeight:600,
            background: profile.role === 'admin' ? '#e8f2eb' : '#e8eef8',
            color: profile.role === 'admin' ? '#2d5a3d' : '#1e4a8c',
            maxWidth:90, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
          }}>
            {profile.name}
          </span>
        </div>
      </div>

      {/* 본문 */}
      <div style={{ flex:1, padding:'20px 16px 32px', overflowY:'auto' }}>
        {renderPage()}
      </div>

      {/* 드로어 메뉴 */}
      {drawerOpen && (
        <div style={{ position:'fixed', inset:0, zIndex:150 }}>
          {/* 배경 */}
          <div onClick={() => setDrawerOpen(false)}
            style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.5)' }} />
          {/* 패널 */}
          <div style={{
            position:'absolute', top:0, left:0, bottom:0, width:260, maxWidth:'80vw',
            background:'#1a1916', color:'#fff', display:'flex', flexDirection:'column',
            boxShadow:'4px 0 30px rgba(0,0,0,0.3)',
          }}>
            <div style={{ padding:'20px 20px 16px', borderBottom:'1px solid rgba(255,255,255,0.1)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <div style={{ fontSize:10, color:'rgba(255,255,255,0.5)', letterSpacing:'0.05em', marginBottom:3 }}>특장차 관리 시스템</div>
                <div style={{ fontSize:15, fontWeight:700 }}>사내 포털</div>
              </div>
              <button type="button" onClick={() => setDrawerOpen(false)}
                style={{ border:'none', background:'rgba(255,255,255,0.1)', color:'#fff', borderRadius:6, width:28, height:28, cursor:'pointer', fontSize:14 }}>
                ✕
              </button>
            </div>

            <nav style={{ padding:'8px 0', flex:1, overflowY:'auto' }}>
              {renderNav(id => { setPage(id); setDrawerOpen(false) })}
            </nav>

            <div style={{ padding:'14px 20px calc(14px + env(safe-area-inset-bottom))', borderTop:'1px solid rgba(255,255,255,0.1)', display:'flex', alignItems:'center', gap:10 }}>
              <div style={{
                width:32, height:32, borderRadius:'50%', background:'#4a8c60', display:'flex',
                alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, flexShrink:0,
              }}>
                {profile.name[0]}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:500 }}>{profile.name}</div>
                <div style={{ fontSize:11, color:'rgba(255,255,255,0.45)', marginTop:1 }}>
                  {profile.role === 'admin' ? '관리자' : profile.dept + ' · 직원'}
                </div>
              </div>
              <button type="button" onClick={() => { setDrawerOpen(false); setPwOpen(true) }}
                title="비밀번호 변경"
                style={{ border:'none', background:'transparent', fontSize:13, color:'rgba(255,255,255,0.5)', cursor:'pointer', padding:'4px 2px', fontFamily:'inherit' }}>
                🔑
              </button>
              <button type="button" onClick={handleLogout}
                style={{ border:'none', background:'transparent', fontSize:11, color:'rgba(255,255,255,0.5)', cursor:'pointer', padding:'4px 6px', fontFamily:'inherit' }}>
                로그아웃
              </button>
            </div>
          </div>
        </div>
      )}
      {pwModal}
    </div>
  )
}
